import { z } from 'zod';
import { GameConfigSchema } from '../game-config';
import {
  UserId,
  PlayerId,
  RoundId,
  EventId,
  ImageId,
  InviteTokenId,
  RequestId,
  SpectatorId,
} from '../ids';
import { PlayerSchema } from '../player';
import { TeamSchema } from '../team';
import { BaseEventSchema } from './base';

export const SessionStartedEventSchema = BaseEventSchema.extend({
  type: z.literal('SessionStarted'),
  payload: z.object({
    name: z.string().min(1).max(200).optional(),
    notes: z.string().max(2000).optional(),
    gameConfigSnapshot: GameConfigSchema,
    playerIds: z.array(z.ulid().brand('PlayerId')).min(1),
    teamConfiguration: z
      .object({
        teams: z.array(
          z.object({
            presetId: z.ulid().brand('TeamId').optional(), // if using preset
            id: z.ulid().brand('TeamId'),
            name: TeamSchema.shape.name,
            nickname: TeamSchema.shape.nickname,
            avatar: z.url().optional(),
            color: z
              .string()
              .regex(/^#[0-9A-Fa-f]{6}$/)
              .optional(),
            playerIds: z.array(z.ulid().brand('PlayerId')).min(1),
          }),
        ),
      })
      .optional(),
  }),
});
export type SessionStartedEvent = z.infer<typeof SessionStartedEventSchema>;

export const SessionEndedEventSchema = BaseEventSchema.extend({
  type: z.literal('SessionEnded'),
  payload: z.object({}),
});
export type SessionEndedEvent = z.infer<typeof SessionEndedEventSchema>;

export const SessionPausedEventSchema = BaseEventSchema.extend({
  type: z.literal('SessionPaused'),
  payload: z.object({}),
});
export type SessionPausedEvent = z.infer<typeof SessionPausedEventSchema>;

export const SessionResumedEventSchema = BaseEventSchema.extend({
  type: z.literal('SessionResumed'),
  payload: z.object({}),
});
export type SessionResumedEvent = z.infer<typeof SessionResumedEventSchema>;

export const SessionRestartedEventSchema = BaseEventSchema.extend({
  type: z.literal('SessionRestarted'),
  payload: z.object({}),
});
export type SessionRestartedEvent = z.infer<typeof SessionRestartedEventSchema>;

export const SessionArchivedEventSchema = BaseEventSchema.extend({
  type: z.literal('SessionArchived'),
  payload: z.object({
    archivedAt: z.number().int().positive(),
  }),
});
export type SessionArchivedEvent = z.infer<typeof SessionArchivedEventSchema>;

export const SessionUnarchivedEventSchema = BaseEventSchema.extend({
  type: z.literal('SessionUnarchived'),
  payload: z.object({}),
});
export type SessionUnarchivedEvent = z.infer<typeof SessionUnarchivedEventSchema>;

export const SessionDeletedEventSchema = BaseEventSchema.extend({
  type: z.literal('SessionDeleted'),
  payload: z.object({}),
});
export type SessionDeletedEvent = z.infer<typeof SessionDeletedEventSchema>;

export const SessionNameChangedEventSchema = BaseEventSchema.extend({
  type: z.literal('SessionNameChanged'),
  payload: z.object({
    name: z
      .string()
      .max(200)
      .refine((value) => value.trim().length > 0, {
        message: 'Name must have at least 1 non-space character',
      }),
  }),
});
export type SessionNameChangedEvent = z.infer<typeof SessionNameChangedEventSchema>;

export const SessionNotesChangedEventSchema = BaseEventSchema.extend({
  type: z.literal('SessionNotesChanged'),
  payload: z.object({
    notes: z.string().max(2000),
  }),
});
export type SessionNotesChangedEvent = z.infer<typeof SessionNotesChangedEventSchema>;

export const SessionOwnershipClaimedEventSchema = BaseEventSchema.extend({
  type: z.literal('SessionOwnershipClaimed'),
  payload: z.object({
    claimedAt: z.number().int().nonnegative(),
    previousOwner: UserId.optional(),
  }),
});
export type SessionOwnershipClaimedEvent = z.infer<typeof SessionOwnershipClaimedEventSchema>;

export const SessionGameConfigChangedEventSchema = BaseEventSchema.extend({
  type: z.literal('SessionGameConfigChanged'),
  payload: z.object({
    gameConfigSnapshot: GameConfigSchema,
  }),
});
export type SessionGameConfigChangedEvent = z.infer<typeof SessionGameConfigChangedEventSchema>;

export const SessionTagAddedEventSchema = BaseEventSchema.extend({
  type: z.literal('SessionTagAdded'),
  payload: z.object({
    tag: z.string().min(1).max(50),
  }),
});
export type SessionTagAddedEvent = z.infer<typeof SessionTagAddedEventSchema>;

export const SessionTagRemovedEventSchema = BaseEventSchema.extend({
  type: z.literal('SessionTagRemoved'),
  payload: z.object({
    tag: z.string().min(1).max(50),
  }),
});
export type SessionTagRemovedEvent = z.infer<typeof SessionTagRemovedEventSchema>;

export const RoundStartedEventSchema = BaseEventSchema.extend({
  type: z.literal('RoundStarted'),
  payload: z.object({
    roundId: RoundId,
    number: z.number().int().positive(),
    name: z.string().min(1).max(100).optional(),
  }),
});
export type RoundStartedEvent = z.infer<typeof RoundStartedEventSchema>;

export const RoundCompletedEventSchema = BaseEventSchema.extend({
  type: z.literal('RoundCompleted'),
  payload: z.object({
    roundId: RoundId,
  }),
});
export type RoundCompletedEvent = z.infer<typeof RoundCompletedEventSchema>;

export const RoundDeletedEventSchema = BaseEventSchema.extend({
  type: z.literal('RoundDeleted'),
  payload: z.object({
    roundId: RoundId,
  }),
});
export type RoundDeletedEvent = z.infer<typeof RoundDeletedEventSchema>;

export const RoundNoteAddedEventSchema = BaseEventSchema.extend({
  type: z.literal('RoundNoteAdded'),
  payload: z.object({
    roundId: RoundId,
    note: z.string().min(1).max(1000),
  }),
});
export type RoundNoteAddedEvent = z.infer<typeof RoundNoteAddedEventSchema>;

export const RoundNoteRemovedEventSchema = BaseEventSchema.extend({
  type: z.literal('RoundNoteRemoved'),
  payload: z.object({
    roundId: RoundId,
  }),
});
export type RoundNoteRemovedEvent = z.infer<typeof RoundNoteRemovedEventSchema>;

export const RoundTagAddedEventSchema = BaseEventSchema.extend({
  type: z.literal('RoundTagAdded'),
  payload: z.object({
    roundId: RoundId,
    tag: z.string().min(1).max(50),
  }),
});
export type RoundTagAddedEvent = z.infer<typeof RoundTagAddedEventSchema>;

export const RoundTagRemovedEventSchema = BaseEventSchema.extend({
  type: z.literal('RoundTagRemoved'),
  payload: z.object({
    roundId: RoundId,
    tag: z.string().min(1).max(50),
  }),
});
export type RoundTagRemovedEvent = z.infer<typeof RoundTagRemovedEventSchema>;

export const ScoreChangedEventSchema = BaseEventSchema.extend({
  type: z.literal('ScoreChanged'),
  payload: z.object({
    roundId: RoundId,
    playerId: PlayerId, // or teamId - validate based on game config
    delta: z.number().int(),
  }),
});
export type ScoreChangedEvent = z.infer<typeof ScoreChangedEventSchema>;

export const ScoreSetEventSchema = BaseEventSchema.extend({
  type: z.literal('ScoreSet'),
  payload: z.object({
    roundId: RoundId,
    playerId: PlayerId,
    value: z.number().int(),
  }),
});
export type ScoreSetEvent = z.infer<typeof ScoreSetEventSchema>;

export const PlayerJoinedEventSchema = BaseEventSchema.extend({
  type: z.literal('PlayerJoined'),
  payload: z.object({
    playerId: PlayerId,
    name: PlayerSchema.shape.name,
    nickname: PlayerSchema.shape.nickname,
    color: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/)
      .optional(),
    entryRound: z.number().int().nonnegative(),
    approvedBy: UserId.optional(), // User who approved the join request (if approval was required)
  }),
});
export type PlayerJoinedEvent = z.infer<typeof PlayerJoinedEventSchema>;

export const PlayerLeftEventSchema = BaseEventSchema.extend({
  type: z.literal('PlayerLeft'),
  payload: z.object({
    playerId: PlayerId,
  }),
});
export type PlayerLeftEvent = z.infer<typeof PlayerLeftEventSchema>;

export const PlayerRemovedEventSchema = BaseEventSchema.extend({
  type: z.literal('PlayerRemoved'),
  payload: z.object({
    playerId: PlayerId,
    removedBy: UserId,
  }),
});
export type PlayerRemovedEvent = z.infer<typeof PlayerRemovedEventSchema>;

export const PlayerOrderChangedEventSchema = BaseEventSchema.extend({
  type: z.literal('PlayerOrderChanged'),
  payload: z.object({
    playerIds: z.array(PlayerId).min(1),
  }),
});
export type PlayerOrderChangedEvent = z.infer<typeof PlayerOrderChangedEventSchema>;

// Spectator events
export const SpectatorJoinedEventSchema = BaseEventSchema.extend({
  type: z.literal('SpectatorJoined'),
  payload: z.object({
    spectatorId: SpectatorId,
    userId: UserId.optional(), // User who joined as spectator (if authenticated)
    approvedBy: UserId.optional(), // User who approved the join request (if approval was required)
  }),
});
export type SpectatorJoinedEvent = z.infer<typeof SpectatorJoinedEventSchema>;

export const SpectatorLeftEventSchema = BaseEventSchema.extend({
  type: z.literal('SpectatorLeft'),
  payload: z.object({
    spectatorId: SpectatorId,
  }),
});
export type SpectatorLeftEvent = z.infer<typeof SpectatorLeftEventSchema>;

const PlayerIdMappingReasonSchema = z.enum(['player_merge', 'user_migration']);
export type PlayerIdMappingReason = z.infer<typeof PlayerIdMappingReasonSchema>;

export const PlayerIdMappedEventSchema = BaseEventSchema.extend({
  type: z.literal('PlayerIdMapped'),
  payload: z.object({
    sourcePlayerId: PlayerId,
    targetPlayerId: PlayerId,
    reason: PlayerIdMappingReasonSchema,
  }),
});
export type PlayerIdMappedEvent = z.infer<typeof PlayerIdMappedEventSchema>;

export const PlayerIdUnmappedEventSchema = BaseEventSchema.extend({
  type: z.literal('PlayerIdUnmapped'),
  payload: z.object({
    originalMappingEventId: EventId,
    sourcePlayerId: PlayerId,
    targetPlayerId: PlayerId,
  }),
});
export type PlayerIdUnmappedEvent = z.infer<typeof PlayerIdUnmappedEventSchema>;

export const EventUndoneEventSchema = BaseEventSchema.extend({
  type: z.literal('EventUndone'),
  payload: z.object({
    undoneEventId: z.ulid().brand('EventId'),
  }),
});
export type EventUndoneEvent = z.infer<typeof EventUndoneEventSchema>;

export const EventRedoneEventSchema = BaseEventSchema.extend({
  type: z.literal('EventRedone'),
  payload: z.object({
    redoneEventId: z.ulid().brand('EventId'),
  }),
});
export type EventRedoneEvent = z.infer<typeof EventRedoneEventSchema>;

export const SessionWinnerSetEventSchema = BaseEventSchema.extend({
  type: z.literal('SessionWinnerSet'),
  payload: z.object({
    winnerIds: z.array(z.union([z.ulid().brand('PlayerId'), z.ulid().brand('TeamId')])),
  }),
});
export type SessionWinnerSetEvent = z.infer<typeof SessionWinnerSetEventSchema>;

// Image events
export const ImageAddedEventSchema = BaseEventSchema.extend({
  type: z.literal('ImageAdded'),
  payload: z.object({
    imageId: ImageId,
    entityType: z.enum(['player', 'session', 'game', 'team', 'round', 'gallery', 'group']),
    entityId: z.string(), // PlayerId | SessionId | GameId | TeamPresetId | RoundId | ImageId (for gallery) | PlayerGroupId
    caption: z.string().max(500).optional(),
    isPrimary: z.boolean().default(false),
    uploadedAt: z
      .string()
      .refine((val) => !Number.isNaN(Date.parse(val)), 'Must be a valid ISO datetime string'), // Timestamp when added (for privacy)
  }),
});
export type ImageAddedEvent = z.infer<typeof ImageAddedEventSchema>;

export const ImageRemovedEventSchema = BaseEventSchema.extend({
  type: z.literal('ImageRemoved'),
  payload: z.object({
    imageId: ImageId,
  }),
});
export type ImageRemovedEvent = z.infer<typeof ImageRemovedEventSchema>;

export const ImageCaptionChangedEventSchema = BaseEventSchema.extend({
  type: z.literal('ImageCaptionChanged'),
  payload: z.object({
    imageId: ImageId,
    caption: z.string().max(500),
  }),
});
export type ImageCaptionChangedEvent = z.infer<typeof ImageCaptionChangedEventSchema>;

export const ImagePrimarySetEventSchema = BaseEventSchema.extend({
  type: z.literal('ImagePrimarySet'),
  payload: z.object({
    entityType: z.enum(['player', 'game', 'team', 'group']),
    entityId: z.string(),
    imageId: ImageId,
  }),
});
export type ImagePrimarySetEvent = z.infer<typeof ImagePrimarySetEventSchema>;

// Invite token events
export const InviteTokenGeneratedEventSchema = BaseEventSchema.extend({
  type: z.literal('InviteTokenGenerated'),
  payload: z.object({
    inviteToken: InviteTokenId,
    generatedBy: UserId,
  }),
});
export type InviteTokenGeneratedEvent = z.infer<typeof InviteTokenGeneratedEventSchema>;

export const InviteTokenRevokedEventSchema = BaseEventSchema.extend({
  type: z.literal('InviteTokenRevoked'),
  payload: z.object({
    previousToken: InviteTokenId,
    revokedBy: UserId,
    reason: z.enum(['regenerated', 'disabled', 'manual']),
  }),
});
export type InviteTokenRevokedEvent = z.infer<typeof InviteTokenRevokedEventSchema>;

export const InviteLinkEnabledEventSchema = BaseEventSchema.extend({
  type: z.literal('InviteLinkEnabled'),
  payload: z.object({
    enabledBy: UserId,
  }),
});
export type InviteLinkEnabledEvent = z.infer<typeof InviteLinkEnabledEventSchema>;

export const InviteLinkDisabledEventSchema = BaseEventSchema.extend({
  type: z.literal('InviteLinkDisabled'),
  payload: z.object({
    disabledBy: UserId,
  }),
});
export type InviteLinkDisabledEvent = z.infer<typeof InviteLinkDisabledEventSchema>;

// Session visibility events
export const SessionPublicSpectatorEnabledEventSchema = BaseEventSchema.extend({
  type: z.literal('SessionPublicSpectatorEnabled'),
  payload: z.object({
    enabledBy: UserId,
  }),
});
export type SessionPublicSpectatorEnabledEvent = z.infer<
  typeof SessionPublicSpectatorEnabledEventSchema
>;

export const SessionPublicSpectatorDisabledEventSchema = BaseEventSchema.extend({
  type: z.literal('SessionPublicSpectatorDisabled'),
  payload: z.object({
    disabledBy: UserId,
  }),
});
export type SessionPublicSpectatorDisabledEvent = z.infer<
  typeof SessionPublicSpectatorDisabledEventSchema
>;

export const SessionPublicPlayerJoinEnabledEventSchema = BaseEventSchema.extend({
  type: z.literal('SessionPublicPlayerJoinEnabled'),
  payload: z.object({
    enabledBy: UserId,
  }),
});
export type SessionPublicPlayerJoinEnabledEvent = z.infer<
  typeof SessionPublicPlayerJoinEnabledEventSchema
>;

export const SessionPublicPlayerJoinDisabledEventSchema = BaseEventSchema.extend({
  type: z.literal('SessionPublicPlayerJoinDisabled'),
  payload: z.object({
    disabledBy: UserId,
  }),
});
export type SessionPublicPlayerJoinDisabledEvent = z.infer<
  typeof SessionPublicPlayerJoinDisabledEventSchema
>;

// Join request events
export const JoinRequestCreatedEventSchema = BaseEventSchema.extend({
  type: z.literal('JoinRequestCreated'),
  payload: z
    .object({
      requestId: RequestId,
      playerId: PlayerId.optional(), // Present if joining as player
      spectatorId: SpectatorId.optional(), // Present if joining as spectator
      userId: UserId.optional(), // Present if joining as authenticated user
      autoApproved: z.boolean(), // true if within 10min/no rounds
    })
    .refine((data) => (data.playerId !== undefined) !== (data.spectatorId !== undefined), {
      message: 'Either playerId or spectatorId must be present, but not both',
    }),
});
export type JoinRequestCreatedEvent = z.infer<typeof JoinRequestCreatedEventSchema>;

export const JoinRequestApprovedEventSchema = BaseEventSchema.extend({
  type: z.literal('JoinRequestApproved'),
  payload: z
    .object({
      requestId: RequestId,
      playerId: PlayerId.optional(), // Present if approving player join
      spectatorId: SpectatorId.optional(), // Present if approving spectator join
      approvedBy: UserId, // User who approved (or system user ID if auto-approved)
    })
    .refine((data) => (data.playerId !== undefined) !== (data.spectatorId !== undefined), {
      message: 'Either playerId or spectatorId must be present, but not both',
    }),
});
export type JoinRequestApprovedEvent = z.infer<typeof JoinRequestApprovedEventSchema>;

export const JoinRequestRejectedEventSchema = BaseEventSchema.extend({
  type: z.literal('JoinRequestRejected'),
  payload: z
    .object({
      requestId: RequestId,
      playerId: PlayerId.optional(), // Present if rejecting player join
      spectatorId: SpectatorId.optional(), // Present if rejecting spectator join
      rejectedBy: UserId,
      reason: z.string().max(500).optional(),
    })
    .refine((data) => (data.playerId !== undefined) !== (data.spectatorId !== undefined), {
      message: 'Either playerId or spectatorId must be present, but not both',
    }),
});
export type JoinRequestRejectedEvent = z.infer<typeof JoinRequestRejectedEventSchema>;

// Union of all session events
export const SessionEventSchema = z.discriminatedUnion('type', [
  SessionStartedEventSchema,
  SessionEndedEventSchema,
  SessionPausedEventSchema,
  SessionResumedEventSchema,
  SessionRestartedEventSchema,
  SessionArchivedEventSchema,
  SessionUnarchivedEventSchema,
  SessionDeletedEventSchema,
  SessionNameChangedEventSchema,
  SessionNotesChangedEventSchema,
  SessionOwnershipClaimedEventSchema,
  SessionGameConfigChangedEventSchema,
  SessionTagAddedEventSchema,
  SessionTagRemovedEventSchema,
  SessionWinnerSetEventSchema,
  RoundStartedEventSchema,
  RoundCompletedEventSchema,
  RoundDeletedEventSchema,
  RoundNoteAddedEventSchema,
  RoundNoteRemovedEventSchema,
  RoundTagAddedEventSchema,
  RoundTagRemovedEventSchema,
  ScoreChangedEventSchema,
  ScoreSetEventSchema,
  PlayerJoinedEventSchema,
  PlayerLeftEventSchema,
  PlayerRemovedEventSchema,
  PlayerOrderChangedEventSchema,
  SpectatorJoinedEventSchema,
  SpectatorLeftEventSchema,
  PlayerIdMappedEventSchema,
  PlayerIdUnmappedEventSchema,
  ImageAddedEventSchema,
  ImageRemovedEventSchema,
  ImageCaptionChangedEventSchema,
  ImagePrimarySetEventSchema,
  InviteTokenGeneratedEventSchema,
  InviteTokenRevokedEventSchema,
  InviteLinkEnabledEventSchema,
  InviteLinkDisabledEventSchema,
  SessionPublicSpectatorEnabledEventSchema,
  SessionPublicSpectatorDisabledEventSchema,
  SessionPublicPlayerJoinEnabledEventSchema,
  SessionPublicPlayerJoinDisabledEventSchema,
  JoinRequestCreatedEventSchema,
  JoinRequestApprovedEventSchema,
  JoinRequestRejectedEventSchema,
  EventUndoneEventSchema,
  EventRedoneEventSchema,
]);
export type SessionEvent = z.infer<typeof SessionEventSchema>;

const stripEventMetadata = <Schema extends z.ZodObject<Record<string, z.ZodTypeAny>>>(
  schema: Schema,
) => schema.omit({ id: true, timestamp: true });

export const NewSessionEventSchema = z.discriminatedUnion('type', [
  stripEventMetadata(SessionStartedEventSchema),
  stripEventMetadata(SessionEndedEventSchema),
  stripEventMetadata(SessionPausedEventSchema),
  stripEventMetadata(SessionResumedEventSchema),
  stripEventMetadata(SessionRestartedEventSchema),
  stripEventMetadata(SessionArchivedEventSchema),
  stripEventMetadata(SessionUnarchivedEventSchema),
  stripEventMetadata(SessionDeletedEventSchema),
  stripEventMetadata(SessionNameChangedEventSchema),
  stripEventMetadata(SessionNotesChangedEventSchema),
  stripEventMetadata(SessionOwnershipClaimedEventSchema),
  stripEventMetadata(SessionGameConfigChangedEventSchema),
  stripEventMetadata(SessionTagAddedEventSchema),
  stripEventMetadata(SessionTagRemovedEventSchema),
  stripEventMetadata(SessionWinnerSetEventSchema),
  stripEventMetadata(RoundStartedEventSchema),
  stripEventMetadata(RoundCompletedEventSchema),
  stripEventMetadata(RoundDeletedEventSchema),
  stripEventMetadata(RoundNoteAddedEventSchema),
  stripEventMetadata(RoundNoteRemovedEventSchema),
  stripEventMetadata(RoundTagAddedEventSchema),
  stripEventMetadata(RoundTagRemovedEventSchema),
  stripEventMetadata(ScoreChangedEventSchema),
  stripEventMetadata(ScoreSetEventSchema),
  stripEventMetadata(PlayerJoinedEventSchema),
  stripEventMetadata(PlayerLeftEventSchema),
  stripEventMetadata(PlayerRemovedEventSchema),
  stripEventMetadata(PlayerOrderChangedEventSchema),
  stripEventMetadata(SpectatorJoinedEventSchema),
  stripEventMetadata(SpectatorLeftEventSchema),
  stripEventMetadata(PlayerIdMappedEventSchema),
  stripEventMetadata(PlayerIdUnmappedEventSchema),
  stripEventMetadata(ImageAddedEventSchema),
  stripEventMetadata(ImageRemovedEventSchema),
  stripEventMetadata(ImageCaptionChangedEventSchema),
  stripEventMetadata(ImagePrimarySetEventSchema),
  stripEventMetadata(InviteTokenGeneratedEventSchema),
  stripEventMetadata(InviteTokenRevokedEventSchema),
  stripEventMetadata(InviteLinkEnabledEventSchema),
  stripEventMetadata(InviteLinkDisabledEventSchema),
  stripEventMetadata(SessionPublicSpectatorEnabledEventSchema),
  stripEventMetadata(SessionPublicSpectatorDisabledEventSchema),
  stripEventMetadata(SessionPublicPlayerJoinEnabledEventSchema),
  stripEventMetadata(SessionPublicPlayerJoinDisabledEventSchema),
  stripEventMetadata(JoinRequestCreatedEventSchema),
  stripEventMetadata(JoinRequestApprovedEventSchema),
  stripEventMetadata(JoinRequestRejectedEventSchema),
  stripEventMetadata(EventUndoneEventSchema),
  stripEventMetadata(EventRedoneEventSchema),
]);
export type NewSessionEvent = z.infer<typeof NewSessionEventSchema>;
